import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { en } from './en.ts';
import { substitute } from './format.ts';
import type { TParams } from './format.ts';
import { DEFAULT_LOCALE, isLocale } from './types.ts';
import type { Locale } from './types.ts';

/**
 * Nxtpath console i18n context — Field Notes rebuild (epic #950 PR-8,
 * formerly the console-shared `createConsoleI18nContext` factory
 * instantiation, specialised to the en-only locale set). The storage
 * key and provider/hook contract are unchanged.
 */

export type I18nKey = keyof typeof en;

const DICTIONARIES: Readonly<Record<Locale, Readonly<Record<I18nKey, string>>>> = { en };

const STORAGE_KEY = 'console.locale';

interface I18nContextValue {
  readonly locale: Locale;
  readonly setLocale: (next: Locale) => void;
  readonly t: (key: I18nKey, params?: TParams) => string;
}

interface I18nProviderProps {
  readonly initialLocale?: Locale;
  readonly children: ReactNode;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readStoredLocale(): Locale | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    return isLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredLocale(next: Locale): void {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Non-fatal: React state still carries the locale for this session.
  }
}

export function I18nProvider({ initialLocale, children }: I18nProviderProps): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (initialLocale !== undefined) {
      return initialLocale;
    }
    return readStoredLocale() ?? DEFAULT_LOCALE;
  });

  const setLocale = useCallback((next: Locale): void => {
    setLocaleState(next);
    writeStoredLocale(next);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.lang = locale;
    const dict = DICTIONARIES[locale];
    document.title = dict['meta.title'];
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription !== null) {
      metaDescription.setAttribute('content', dict['meta.description']);
    }
  }, [locale]);

  const t = useMemo(() => {
    const dict = DICTIONARIES[locale];
    return (key: I18nKey, params?: TParams): string => substitute(dict[key], params);
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): (key: I18nKey, params?: TParams) => string {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error('useT must be used inside <I18nProvider>');
  }
  return ctx.t;
}

export function useLocale(): {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
} {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error('useLocale must be used inside <I18nProvider>');
  }
  return { locale: ctx.locale, setLocale: ctx.setLocale };
}
